import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Building2, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader, EmptyState, ConfirmDialog, Field } from "../components/common";
import { api, errMsg } from "../lib/api";

const EMPTY = { nama: "", alamat: "", catatan: "" };

const PerusahaanDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(initial ? {
      nama: initial.nama || "", alamat: initial.alamat || "", catatan: initial.catatan || "",
    } : EMPTY);
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.nama.trim()) return toast.error("Nama perusahaan wajib diisi");
    setSaving(true);
    try {
      const body = { nama: f.nama.trim(), alamat: f.alamat.trim() || null, catatan: f.catatan.trim() || null };
      if (initial) await api.put(`/perusahaan/${initial.id}`, body);
      else await api.post("/perusahaan", body);
      toast.success(initial ? "Perusahaan diperbarui" : "Perusahaan ditambahkan");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="perusahaan-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Ubah perusahaan" : "Tambah perusahaan"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nama perusahaan"><Input data-testid="perusahaan-nama-input" className="h-10" value={f.nama} onChange={(e) => setF({ ...f, nama: e.target.value })} autoFocus /></Field>
          <Field label="Alamat (opsional)"><Input data-testid="perusahaan-alamat-input" className="h-10" value={f.alamat} onChange={(e) => setF({ ...f, alamat: e.target.value })} /></Field>
          <Field label="Catatan (opsional)"><Textarea data-testid="perusahaan-catatan-input" value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} /></Field>
          <DialogFooter>
            <Button data-testid="perusahaan-save-btn" type="submit" disabled={saving} className="h-10 w-full rounded-full sm:w-auto">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function PerusahaanPage() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const load = useCallback(() => api.get("/perusahaan").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try { await api.delete(`/perusahaan/${del.id}`); toast.success("Perusahaan dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };

  return (
    <div>
      <PageHeader title="Perusahaan" testid="perusahaan-title" subtitle="Tempat penempatan anak-anak."
        actions={<Button data-testid="perusahaan-add-btn" className="h-10 rounded-full" onClick={() => setDialog({ open: true, item: null })}><Plus className="mr-1.5 h-4 w-4" />Tambah perusahaan</Button>} />
      {items && items.length === 0 && <EmptyState icon={Building2} title="Belum ada perusahaan" desc="Tambahkan perusahaan pertama Anda." />}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(items || []).map((p, i) => (
          <div key={p.id} data-testid={`perusahaan-card-${p.id}`} style={{ animationDelay: `${i * 40}ms` }} className="fade-up flex flex-col rounded-xl border border-gray-200 bg-white p-5 transition-shadow duration-200 hover:shadow-md">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-heading text-lg font-semibold text-gray-900">{p.nama}</p>
                {p.alamat && <p className="mt-1 flex items-center gap-1 text-sm text-gray-500"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.alamat}</span></p>}
              </div>
              <span className="shrink-0 rounded-full bg-secondary px-2.5 py-1 font-mono text-xs font-semibold text-primary">{p.jumlah_anak} anak</span>
            </div>
            {p.catatan && <p className="mt-3 text-sm text-gray-600">{p.catatan}</p>}
            <div className="mt-4 flex gap-2 border-t border-gray-100 pt-4">
              <Button data-testid={`perusahaan-edit-${p.id}`} variant="outline" className="h-9 flex-1" onClick={() => setDialog({ open: true, item: p })}><Pencil className="mr-1.5 h-4 w-4" />Ubah</Button>
              <Button data-testid={`perusahaan-delete-${p.id}`} variant="outline" className="h-9 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDel(p)} aria-label="Hapus"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <PerusahaanDialog open={dialog.open} initial={dialog.item} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title="Hapus perusahaan?"
        description={`Perusahaan "${del?.nama}" akan dihapus. Perusahaan yang sudah memiliki voucher tidak bisa dihapus.`} confirmLabel="Hapus" onConfirm={doDelete} testid="perusahaan-delete-confirm" />
    </div>
  );
}
